import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { categoryDef } from '@/lib/constants';
import { formatVND, formatPercent } from '@/lib/format';
import type { Monitor } from '@/lib/api';

export interface ReportSkuTableProps {
  title: string;
  monitors: Monitor[];
  showPrices?: boolean;
}

export function ReportSkuTable({ title, monitors, showPrices = true }: ReportSkuTableProps) {
  if (monitors.length === 0) {
    return (
      <section className="print-no-break">
        <h2 className="mb-2 text-sm font-semibold">{title}</h2>
        <div className="rounded-lg border border-dashed bg-muted/30 p-6 text-center text-xs text-muted-foreground">
          Không có SKU nào trong nhóm này.
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold">
        {title} <span className="text-muted-foreground">({monitors.length})</span>
      </h2>

      <div className="overflow-x-auto rounded-lg border bg-card print:rounded-md print:shadow-none">
        <table className="w-full text-[11px]">
          <thead className="bg-primary text-primary-foreground print:bg-primary">
            <tr>
              <th className="sticky left-0 z-30 bg-primary px-2 py-2 text-left font-semibold uppercase tracking-wider">#</th>
              <th className="sticky left-[34px] z-30 bg-primary px-2 py-2 text-left font-semibold uppercase tracking-wider shadow-[4px_0_6px_-2px_rgba(0,0,0,0.18)]">SKU</th>
              <th className="px-2 py-2 text-left font-semibold uppercase tracking-wider">Sản phẩm</th>
              <th className="px-2 py-2 text-left font-semibold uppercase tracking-wider">Phân khúc</th>
              <th className="px-2 py-2 text-right font-semibold uppercase tracking-wider">Size</th>
              <th className="px-2 py-2 text-center font-semibold uppercase tracking-wider">PG</th>
              <th className="px-2 py-2 text-right font-semibold uppercase tracking-wider">Hz</th>
              <th className="px-2 py-2 text-left font-semibold uppercase tracking-wider">Panel</th>
              {showPrices && (
                <>
                  <th className="px-2 py-2 text-right font-semibold uppercase tracking-wider">Giá CT</th>
                  <th className="px-2 py-2 text-right font-semibold uppercase tracking-wider">Giá Web</th>
                  <th className="px-2 py-2 text-right font-semibold uppercase tracking-wider">% LN</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {monitors.map((m, idx) => {
              const cat = categoryDef(m.category);
              return (
                <tr
                  key={m.id}
                  className={cn(
                    'border-b border-border print-no-break',
                    idx % 2 === 1 && 'bg-muted/40 print:bg-muted/30',
                    m.is_focal === 1 && 'bg-cat-focal/5 print:bg-cat-focal/5'
                  )}
                >
                  {/* # + SKU sticky cells share the same opaque light-purple bg so they read as one block.
                      Shadow only on right edge of SKU cell (boundary with scrolling content). */}
                  <td className="sticky left-0 z-20 bg-[hsl(262_60%_97%)] px-2 py-1.5 tabular-nums text-muted-foreground">
                    {idx + 1}
                  </td>
                  <td className="sticky left-[34px] z-20 bg-[hsl(262_60%_97%)] px-2 py-1.5 shadow-[4px_0_6px_-2px_rgba(0,0,0,0.12)]">
                    <div className="flex items-center gap-1">
                      {m.is_focal === 1 && <Star className="h-2.5 w-2.5 fill-cat-focal text-cat-focal" />}
                      <span>{m.id}</span>
                    </div>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="font-medium leading-tight">{m.name}</div>
                  </td>
                  <td className="px-2 py-1.5">
                    {cat ? (
                      <div className="flex items-center gap-1">
                        <cat.icon className={cn('h-3 w-3', cat.colorClass)} />
                        <span>{cat.label}</span>
                      </div>
                    ) : (
                      m.category
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {m.size ? `${m.size}"` : '—'}
                  </td>
                  <td className="px-2 py-1.5 text-center">{m.resolution ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{m.refresh_rate ?? '—'}</td>
                  <td className="px-2 py-1.5 text-muted-foreground">{m.panel_type ?? '—'}</td>
                  {showPrices && (
                    <>
                      <td className="px-2 py-1.5 text-right tabular-nums">{formatVND(m.price_ct)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-primary">
                        {formatVND(m.price_web)}
                      </td>
                      <td
                        className={cn(
                          'px-2 py-1.5 text-right tabular-nums',
                          m.margin_pct != null && m.margin_pct < 0 && 'text-destructive',
                          m.margin_pct != null && m.margin_pct >= 10 && 'text-cat-graphic'
                        )}
                      >
                        {formatPercent(m.margin_pct)}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
